import axios from 'axios';
import { A_VERIFICAR, VERIFICADO, RECHAZADO } from 'qreventos-suite/constantes';
import defaultRulesJson from '@/data/rules.json';

export interface AutoVerifyRules {
  emailDomains: string[];
  companyKeywords: string[];
  tourismKeywords: string[];
}

export interface RejectRules {
  occupationKeywords: string[];
  genericEmailDomains: string[];
  suspiciousCompanies: string[];
  disallowedOtherKeywords: string[];
  garbagePatterns: string[];
}

export interface ValidationRules {
  autoVerify: AutoVerifyRules;
  reject: RejectRules;
}

export interface ValidationResult {
  estadoId: number;
  motivo: string;
}

export const EXTERNAL_RULES_URL = 'https://acreditaciones.qreventos.com/fit_data/rules.json';

let cachedRules: ValidationRules = defaultRulesJson as ValidationRules;

/**
 * Obtiene las reglas del servidor externo con fallback a rules.json local en src/data.
 */
export async function fetchValidationRules(): Promise<ValidationRules> {
  try {
    const response = await axios.get<ValidationRules>(EXTERNAL_RULES_URL, {
      timeout: 4000,
      headers: {
        'Cache-Control': 'no-cache'
      }
    });

    if (response.status === 200 && response.data && response.data.autoVerify && response.data.reject) {
      cachedRules = response.data;
      console.log('[ValidationEngine] Reglas cargadas correctamente desde el servidor externo:', EXTERNAL_RULES_URL);
    } else {
      console.warn('[ValidationEngine] El JSON externo no posee la estructura requerida. Usando rules.json local.');
      cachedRules = defaultRulesJson as ValidationRules;
    }
  } catch (error) {
    console.warn('[ValidationEngine] No se pudo descargar rules.json desde el servidor externo (o no existe aún). Utilizando rules.json local de data:', error);
    cachedRules = defaultRulesJson as ValidationRules;
  }
  return cachedRules;
}

/**
 * Normaliza cadenas de texto eliminando acentos y convirtiendo a minúsculas.
 */
function normalizeText(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

// =====================================================================
// Fuzzy Matching: Distancia de Levenshtein + coincidencia por palabras
// =====================================================================

/**
 * Calcula la distancia de Levenshtein entre dos cadenas.
 * Mide el número mínimo de ediciones (inserción, eliminación, sustitución)
 * necesarias para transformar una cadena en otra.
 */
function levenshtein(a: string, b: string): number {
  const la = a.length;
  const lb = b.length;
  if (la === 0) return lb;
  if (lb === 0) return la;

  // Usar solo dos filas para optimizar memoria
  let prevRow = new Array(lb + 1);
  let currRow = new Array(lb + 1);

  for (let j = 0; j <= lb; j++) prevRow[j] = j;

  for (let i = 1; i <= la; i++) {
    currRow[0] = i;
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      currRow[j] = Math.min(
        currRow[j - 1] + 1,      // inserción
        prevRow[j] + 1,          // eliminación
        prevRow[j - 1] + cost    // sustitución
      );
    }
    [prevRow, currRow] = [currRow, prevRow];
  }

  return prevRow[lb];
}

/**
 * Determina el umbral de tolerancia según la longitud de la palabra.
 * - Palabras cortas (<=3 chars): sin tolerancia (debe ser exacta)
 * - Palabras de 4-5 chars: 1 error
 * - Palabras de 6-8 chars: 2 errores
 * - Palabras de 9+ chars: 3 errores
 */
function getFuzzyThreshold(wordLength: number): number {
  if (wordLength <= 3) return 0;
  if (wordLength <= 5) return 1;
  if (wordLength <= 8) return 2;
  return 3;
}

/**
 * Comprueba si dos palabras son similares usando distancia de Levenshtein.
 */
function isFuzzyWordMatch(word: string, target: string): boolean {
  const threshold = getFuzzyThreshold(target.length);
  if (threshold === 0) {
    return word === target;
  }
  const dist = levenshtein(word, target);
  return dist <= threshold;
}

/**
 * Comprueba si un texto contiene una keyword, usando coincidencia fuzzy.
 * 
 * Estrategia:
 * 1. Primero prueba coincidencia exacta (substring) - la más rápida.
 * 2. Si la keyword es una sola palabra, busca coincidencia fuzzy contra
 *    cada palabra del texto.
 * 3. Si la keyword es multi-palabra (ej. "colonia de vacaciones"),
 *    verifica si suficientes palabras significativas de la keyword
 *    están presentes (exactas o fuzzy) en el texto. Las palabras
 *    de conexión ("de", "del", "la", "el", "y", "en", "los", "las")
 *    son ignoradas en la evaluación.
 */
function fuzzyContains(text: string, keyword: string): boolean {
  // 1. Coincidencia exacta por substring (rápido)
  if (text.includes(keyword)) {
    return true;
  }

  const textWords = text.split(/\s+/).filter(w => w.length > 0);
  const keywordWords = keyword.split(/\s+/).filter(w => w.length > 0);

  // Palabras de conexión que se ignoran para el matching multi-palabra
  const stopWords = new Set(['de', 'del', 'la', 'el', 'y', 'en', 'los', 'las', 'un', 'una', 'por', 'para', 'con', 'a']);

  // 2. Keyword de una sola palabra: buscar fuzzy contra cada palabra del texto
  if (keywordWords.length === 1) {
    const kw = keywordWords[0];
    for (const tw of textWords) {
      if (isFuzzyWordMatch(tw, kw)) {
        return true;
      }
    }
    return false;
  }

  // 3. Keyword multi-palabra: verificar que las palabras significativas estén presentes
  const significantKeywords = keywordWords.filter(w => !stopWords.has(w));

  // Si no hay palabras significativas (muy raro), usar todas
  const wordsToMatch = significantKeywords.length > 0 ? significantKeywords : keywordWords;

  let matchedCount = 0;
  for (const kw of wordsToMatch) {
    let found = false;
    for (const tw of textWords) {
      if (isFuzzyWordMatch(tw, kw)) {
        found = true;
        break;
      }
    }
    if (found) {
      matchedCount++;
    }
  }

  // Se requiere que al menos el 70% de las palabras significativas coincidan,
  // con un mínimo de 1 palabra. Para frases de 2 palabras significativas,
  // ambas deben coincidir.
  const requiredMatches = wordsToMatch.length <= 2
    ? wordsToMatch.length
    : Math.ceil(wordsToMatch.length * 0.7);

  return matchedCount >= requiredMatches;
}

/**
 * Comprueba si un texto contiene algún patrón de basura / texto aleatorio.
 */
function isGarbageText(text: string, patterns: string[]): boolean {
  const norm = normalizeText(text);
  if (!norm) return true;

  // Secuencias repetitivas conocidas
  for (const pattern of patterns) {
    if (norm.includes(normalizeText(pattern))) {
      return true;
    }
  }

  // Caracteres idénticos repetidos (ej. "aaaaa", "zzzzz")
  if (/^([a-z0-9])\1{3,}$/i.test(norm)) {
    return true;
  }

  return false;
}

/**
 * Evalúa las reglas de pre-validación para un nuevo usuario que se registra.
 * Retorna estadoId (1 = A_VERIFICAR, 2 = VERIFICADO, 3 = RECHAZADO) y motivo.
 */
export function evaluarEstadoAcreditado(formData: any, rulesInput?: ValidationRules): ValidationResult {
  const rules = rulesInput || cachedRules;

  const nombreNorm = normalizeText(formData.nombre);
  const apellidoNorm = normalizeText(formData.apellido);
  const empresaNorm = normalizeText(formData.empresa);
  const cargoNorm = normalizeText(formData.cargo);
  const mailNorm = normalizeText(formData.mail);

  // Extraer dominio de mail
  const mailParts = mailNorm.split('@');
  const mailDomain = mailParts.length > 1 ? mailParts[1] : '';

  // Extraer nombres de rubros seleccionados
  const rubrosText = Array.isArray(formData.rubros)
    ? formData.rubros.map((r: any) => normalizeText(typeof r === 'string' ? r : r.descripcion)).join(' ')
    : normalizeText(formData.rubros);

  // --- REGLAS DE RECHAZO (RECHAZADO = 3) ---

  // 1. Palabras clave en Ocupación / Cargo (fuzzy)
  for (const kw of rules.reject.occupationKeywords) {
    const kwNorm = normalizeText(kw);
    if (fuzzyContains(cargoNorm, kwNorm)) {
      return {
        estadoId: RECHAZADO,
        motivo: `RECHAZADO por Ocupación/Cargo no elegible [Regla: reject.occupationKeywords -> "${kw}"] en campo Cargo ("${formData.cargo}")`
      };
    }
  }

  // 2. Dominio de mail genérico + Nombre de empresa sospechoso o nombre propio repetido
  const isGenericEmail = rules.reject.genericEmailDomains.some(domain => mailDomain === normalizeText(domain));
  if (isGenericEmail) {
    // Empresa es "particular", "monotributista", etc. (fuzzy)
    const matchedSuspiciousComp = rules.reject.suspiciousCompanies.find(sc => fuzzyContains(empresaNorm, normalizeText(sc)));
    
    // Empresa repite el nombre y/o apellido del usuario
    const fullNameNorm = `${nombreNorm} ${apellidoNorm}`.trim();
    const repeatsOwnName = (nombreNorm && empresaNorm === nombreNorm) ||
                           (apellidoNorm && empresaNorm === apellidoNorm) ||
                           (fullNameNorm && empresaNorm === fullNameNorm);

    if (matchedSuspiciousComp) {
      return {
        estadoId: RECHAZADO,
        motivo: `RECHAZADO por Mail Genérico (@${mailDomain}) + Empresa Sospechosa [Regla: reject.suspiciousCompanies -> "${matchedSuspiciousComp}"] en Empresa ("${formData.empresa}")`
      };
    }

    if (repeatsOwnName) {
      return {
        estadoId: RECHAZADO,
        motivo: `RECHAZADO por Mail Genérico (@${mailDomain}) + Nombre propio repetido en Empresa ("${formData.empresa}")`
      };
    }
  }

  // 3. Campos con texto basura o inconsistente en Empresa o Cargo
  if (isGarbageText(empresaNorm, rules.reject.garbagePatterns)) {
    return {
      estadoId: RECHAZADO,
      motivo: `RECHAZADO por Texto Inconsistente/Basura [Regla: reject.garbagePatterns] en Empresa ("${formData.empresa}")`
    };
  }

  if (isGarbageText(cargoNorm, rules.reject.garbagePatterns)) {
    return {
      estadoId: RECHAZADO,
      motivo: `RECHAZADO por Texto Inconsistente/Basura [Regla: reject.garbagePatterns] en Cargo ("${formData.cargo}")`
    };
  }



  // --- REGLAS DE AUTO-VERIFICACIÓN (VERIFICADO = 2) ---

  // 1. Dominio de correo oficial de turismo (.tur.ar o .tur) - exacto, no fuzzy
  for (const turDomain of rules.autoVerify.emailDomains) {
    const tdNorm = normalizeText(turDomain);
    if (mailNorm.endsWith(tdNorm)) {
      return {
        estadoId: VERIFICADO,
        motivo: `VERIFICADO por Dominio Oficial de Turismo [Regla: autoVerify.emailDomains -> "${turDomain}"] en Mail ("${formData.mail}")`
      };
    }
  }

  // 2. Reconocer empresas de turismo por marcas (fuzzy)
  for (const compKw of rules.autoVerify.companyKeywords) {
    if (fuzzyContains(empresaNorm, normalizeText(compKw))) {
      return {
        estadoId: VERIFICADO,
        motivo: `VERIFICADO por Empresa/Marca de Turismo reconocida [Regla: autoVerify.companyKeywords -> "${compKw}"] en Empresa ("${formData.empresa}")`
      };
    }
  }

  // 3. Palabras clave de turismo en empresa o cargo (fuzzy)
  for (const turKw of rules.autoVerify.tourismKeywords) {
    const turKwNorm = normalizeText(turKw);
    if (fuzzyContains(empresaNorm, turKwNorm)) {
      return {
        estadoId: VERIFICADO,
        motivo: `VERIFICADO por Palabra clave de turismo [Regla: autoVerify.tourismKeywords -> "${turKw}"] en Empresa ("${formData.empresa}")`
      };
    }
    if (fuzzyContains(cargoNorm, turKwNorm)) {
      return {
        estadoId: VERIFICADO,
        motivo: `VERIFICADO por Palabra clave de turismo [Regla: autoVerify.tourismKeywords -> "${turKw}"] en Cargo ("${formData.cargo}")`
      };
    }
  }

  // --- ESTADO POR DEFECTO (A_VERIFICAR = 1) ---
  return {
    estadoId: A_VERIFICAR,
    motivo: 'A_VERIFICAR por Defecto (Sin coincidencias en reglas de Rechazo ni Auto-Verificación)'
  };
}
